import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useCart } from "../../context/CartContext";
import productService from "../../api/services/product.service";
import { getProductImages } from "../../utils/imageUtils";

import './Home.css';
import '../Products/Products.css';

import Marquee from "../../components/Marquee/Marquee";

export default function Home() {
  const { addToCart, openSidebar } = useCart();
  const navigate = useNavigate();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        const res = await productService.userList({ page: 0, limit: 8 });
        const data = res?.data?.docs || res?.data || [];
        const mapped = data.map(p => {
          const images = getProductImages(p);
          const productId = p.uuid || p._id || p.product_code || p.id;

          // Preserve variants so list cards can build a valid cart payload.
          const variants = Array.isArray(p.variants) ? p.variants : [];

          // Filter for valid variants (price > 0) and sort by quantity
          const validVariants = variants
            .filter((v) => {
              const vp = v?.variant_price ?? v?.price;
              const num = Number(vp);
              return !isNaN(num) && num > 0;
            })
            .sort((a, b) => {
              const getQuantity = (v) => {
                if (Array.isArray(v.attributes)) {
                  return v.attributes[0]?.attribute_value || "";
                }
                return Object.values(v.attributes || {})[0] || "";
              };
              const aVal = parseFloat(getQuantity(a));
              const bVal = parseFloat(getQuantity(b));
              if (!isNaN(aVal) && !isNaN(bVal)) return aVal - bVal;
              return String(getQuantity(a)).localeCompare(String(getQuantity(b)));
            });

          // Pick a default variant for quick-add (prefer Pack Quantity = 3 if present)
          const defaultVariant =
            validVariants.find(
              (v) =>
                Array.isArray(v?.attributes) &&
                v.attributes.some(
                  (a) =>
                    a?.attribute_type === "Pack Quantity" &&
                    String(a?.attribute_value).trim() === "3"
                )
            ) ||
            validVariants[0] ||
            variants[0];

          const defaultVariantUuid =
            defaultVariant?.variant_uuid || defaultVariant?.uuid || defaultVariant?.variant_id;

          // Default subscription duration: 1 week (7 days) if available, else first available duration
          const durations = Array.isArray(defaultVariant?.billing_durations)
            ? defaultVariant.billing_durations
            : [];
          const defaultBillingDuration = durations.includes(7)
            ? 7
            : durations.length
            ? durations[0]
            : undefined;

          const displayPrice = validVariants.length
            ? parseFloat(defaultVariant?.variant_price || defaultVariant?.price || 0)
            : typeof p.price === "number"
            ? p.price
            : Number(p.price) || 0;

          return {
            id: productId,
            name: p.name,
            price: displayPrice,
            originalPrice: p.original_price,
            image: images.length > 0 ? images[0] : null,
            benefit: p.how_to_use || "Clean energy • No crash",
            inStock: p.stock > 0,
            variants,
            defaultVariantUuid,
            defaultBillingDuration,
            // As requested: quick-add defaults to subscription (1 week if available)
            defaultPurchaseType: "subscription",
          };
        });
        setFeaturedProducts(mapped);
      } catch (err) {
        console.error("Failed to fetch featured products:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchFeaturedProducts();
  }, []);

  const handleAddToCart = async (product) => {
    // Build a valid cart payload for quick-add from list cards
    const payload = {
      item_uuid: product?.id,
      variant_uuid: product?.defaultVariantUuid,
      quantity: 1,
      purchase_type: product?.defaultPurchaseType || "subscription",
      ...(product?.defaultBillingDuration ? { billing_duration: product.defaultBillingDuration } : {}),
    };

    await addToCart(payload);
    openSidebar();
  };

  // Logic to fill at least 8 slots for the 3D carousel
  const carouselDisplayItems = featuredProducts.length > 0 
    ? (featuredProducts.length < 8 
        ? Array.from({ length: 8 }, (_, i) => ({
            ...featuredProducts[i % featuredProducts.length],
            displayId: `carousel-${featuredProducts[i % featuredProducts.length].id}-${i}`
          }))
        : featuredProducts.map(p => ({ ...p, displayId: p.id })))
    : [];

  return (
    <>
      {/* Hero Section - Luxury Minimal Experience */}
      <section className="section home-hero-section" style={{ position: 'relative' }}>
        {/* Hero Background Video */}
        <video
          autoPlay
          muted
          loop
          playsInline
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
        >
          <source src="/chilltree-images/Hero section video.mp4" type="video/mp4" />
        </video>

        {/* Cinematic Obsidian Overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(135deg, rgba(11, 15, 26, 0.3) 0%, rgba(17, 22, 42, 0.25) 25%, rgba(26, 31, 58, 0.2) 50%, rgba(11, 15, 26, 0.3) 100%)',
          zIndex: 1,
        }} />

        {/* Liquid Gold Mist - Luxury Effect */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'radial-gradient(circle at 30% 20%, rgba(0, 255, 255, 0.08) 0%, transparent 50%), radial-gradient(circle at 70% 80%, rgba(0, 255, 255, 0.06) 0%, transparent 50%)',
          zIndex: 2,
        }} />

        {/* Premium Grain Overlay */}
        <div className="grain-overlay" style={{ zIndex: 3 }} />

        <div className="image-section-content" style={{ zIndex: 4 }}>
          <div className="premium-container">
            {/* Professional Headline */}
            <h1
              className="hero-heading fade-in-up"
              style={{
                fontSize: 'clamp(3.5rem, 7vw, 4rem)',
                fontWeight: 900,
                margin: '0 0 2rem 0',
                lineHeight: 1.1,
                letterSpacing: '-0.04em',
                textTransform: 'uppercase',
                color: '#fff',
                background: 'none',
                WebkitTextFillColor: '#fff',
                textShadow: '0 4px 12px rgba(0,0,0,0.4)',
                maxWidth: '800px',
                marginLeft: 'auto',
                marginRight: 'auto'
              }}
            >
              <span
                style={{
                  color: '#00FFFF',
                  background: 'none',
                  WebkitTextFillColor: '#00FFFF',
                  textShadow: '0 4px 12px rgba(0, 0, 0, 0.5), 0 0 25px rgba(0, 255, 255, 0.5)',
                }}
              >
                Powerful
              </span>{' '}
              <span
                style={{
                  color: '#00FFFF',
                  textShadow: '0 4px 12px rgba(0, 0, 0, 0.5), 0 0 25px rgba(0, 255, 255, 0.5)',
                  background: 'none',
                  WebkitTextFillColor: '#FFFFFF',
                }}
              >
                Energy
              </span>
              <br />
              <span
                style={{
                  fontSize: 'clamp(2.5rem, 5vw, 3rem)',
                  display: 'block',
                  marginTop: '0.5rem',
                  color: '#fff',
                  background: 'none',
                  WebkitTextFillColor: '#fff',
                }}
              >
                Absolute Focus.
              </span>
            </h1>

            {/* Professional Dual CTA */}
            <div className="fade-in-up" style={{
              animationDelay: '0.6s',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '1.5rem',
            }}>
              <Link to="/products" className="premium-btn hover-lift" style={{
                textDecoration: 'none',
                textAlign: 'center',
                fontSize: '0.9375rem',
                padding: '1.125rem 3rem',
                minWidth: '280px',
                background: 'transparent',
                backdropFilter: 'blur(8px)',
                color: '#FFFFFF',
                fontWeight: 600,
                letterSpacing: '0.08em',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 4px 15px rgba(255, 255, 255, 0.1)',
              }}>
                Start My Subscription
              </Link>
              <Link to="/products" className="premium-btn premium-btn-secondary hover-glow" style={{
                textDecoration: 'none',
                textAlign: 'center',
                fontSize: '0.9375rem',
                padding: '1rem 2.5rem',
                minWidth: '250px',
                color: '#FFFFFF',
                borderColor: 'rgba(255,255,255,0.3)',
                fontWeight: 600,
                letterSpacing: '0.08em',
              }}>
                One-time Purchase
              </Link>
            </div>

            {/* Hero Features */}
            <div className="hero-features fade-in-up" style={{
              animationDelay: '0.8s',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 'clamp(2rem, 5vw, 4rem)',
              marginTop: '2.5rem',
              flexWrap: 'wrap',
            }}>
              <div className="hero-feature" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#00FFFF',
                fontSize: 'clamp(0.875rem, 1.5vw, 1rem)',
                fontWeight: '600',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
              }}>
                <span style={{ fontSize: '1.2em' }}>🔬</span>
                <span>Lab Tested</span>
              </div>
              
              <div className="hero-feature" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#00FFFF',
                fontSize: 'clamp(0.875rem, 1.5vw, 1rem)',
                fontWeight: '600',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
              }}>
                <span style={{ fontSize: '1.2em' }}>🌿</span>
                <span>Made with Natural Botanicals</span>
              </div>
              
              <div className="hero-feature" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#00FFFF',
                fontSize: 'clamp(0.875rem, 1.5vw, 1rem)',
                fontWeight: '600',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
              }}>
                <span style={{ fontSize: '1.2em' }}>🚚</span>
                <span>Free Shipping over $50</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Premium Marquee */}
      <Marquee />

      {/* Categories section removed by request */}

      {/* Premium Collection - Hero Section Format */}
      <section className="premium-section premium-section--professional" style={{
        position: 'relative',
        paddingTop: '72px',
        paddingBottom: '72px',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #0a1628 0%, #1a2332 100%)',
      }}>
        {/* Cinematic Blue Overlay - Enhanced Premium */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(135deg, rgba(8, 12, 24, 0.6) 0%, rgba(15, 20, 35, 0.5) 25%, rgba(20, 28, 48, 0.4) 50%, rgba(25, 35, 60, 0.3) 75%, rgba(30, 42, 70, 0.2) 100%)',
          zIndex: 1,
        }} />

        {/* Premium Silver Mist - Luxury Effect */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'radial-gradient(circle at 30% 20%, rgba(0, 255, 255, 0.12) 0%, transparent 50%), radial-gradient(circle at 70% 80%, rgba(0, 255, 255, 0.08) 0%, transparent 50%)',
          zIndex: 2,
        }} />

        {/* Premium Particle Effect */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.02) 0%, transparent 70%)',
          zIndex: 3,
        }} />

        <div style={{ 
          position: 'relative', 
          zIndex: 4,
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 20px'
        }}>
          {/* Heading Area - Centered Layout */}
          <div className="section-header fade-in-up" style={{
            animationDelay: '0.2s',
            textAlign: 'center',
            marginBottom: '36px'
          }}>
            <h2 className="section-title" style={{
              fontSize: 'clamp(2rem, 4.5vw, 2.5rem)',
              fontWeight: 800,
              marginBottom: '0.25rem',
              color: '#ffffff',
              textShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
              lineHeight: 1.2,
              letterSpacing: '-0.03em',
            }}>
              Professional <span style={{
                color: '#00E6E6',
                textShadow: '0 4px 8px rgba(0, 0, 0, 0.3), 0 0 25px rgba(0, 230, 230, 0.5)',
              }}>Collection</span>
            </h2>
            <div className="premium-divider" style={{ 
              margin: '0.25rem auto',
              width: '80px',
              height: '2px',
              background: 'linear-gradient(90deg, transparent, #00E6E6, transparent)',
              boxShadow: '0 0 20px rgba(0, 230, 230, 0.5)'
            }} />
            <p className="section-subtitle" style={{
              fontSize: 'clamp(1rem, 2vw, 1.125rem)',
              color: '#e8e8e8',
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: 1.6,
              fontWeight: 500,
            }}>
              Engineered for energy, crafted for peak performance.
            </p>
          </div>

          {/* 3D Carousel with Trust Badge */}
          <div
            className="fade-in-up professional-collection-row"
            style={{
              animationDelay: '0.6s',
              display: 'flex',
              alignItems: 'center',
              gap: '40px',
              marginTop: '24px',
              marginBottom: '28px',
            }}
          >
            {/* 3D Circular Carousel */}
            <div className="professional-carousel-slot">
              <div className="circular-carousel" style={{ height: '100%' }}>
                <div className="carousel-container">
                  {loading ? (
                    <div className="products-loading">
                      <div className="loading-spinner"></div>
                    </div>
                  ) : carouselDisplayItems.length > 0 ? (
                    carouselDisplayItems.map((product, index) => (
                    <div
                      key={product.displayId}
                      className="carousel-card"
                      style={{
                        '--card-index': index,
                        '--total-cards': carouselDisplayItems.length,
                      }}
                    >
                      <div className="product-card">
                        <div className="card-face">
                          <div className="card-image-container">
                            <img 
                              src={product.image || '/chilltree-images/chill-tree-gold.png'} 
                              alt={product.name}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                display: 'block',
                                visibility: 'visible',
                                opacity: 1
                              }}
                              onError={(e) => {
                                if (!e.target.src.includes('chill-tree-gold.png')) {
                                  e.target.src = '/chilltree-images/chill-tree-gold.png';
                                }
                              }}
                            />
                          </div>
                          <h3 className="card-title">{product.name}</h3>
                          <div className="price-block">
                            <div className="current-price">${Number(product.price).toFixed(2)}</div>
                          </div>
                          <button 
                            className="add-to-cart-btn"
                            onClick={() => handleAddToCart(product)}
                          >
                            Add to Cart
                          </button>
                        </div>
                      </div>
                    </div>
                    ))
                  ) : (
                    <div className="products-empty">
                      <p>No products available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Trust Badge */}
            <div className="professional-collection-aside" style={{ display: 'flex', flexDirection: 'column', gap: '16px', flexShrink: 0 }}>
              <div className="premium-trust-badge" style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '1.5rem',
                padding: '1.25rem 2.5rem',
                background: 'transparent',
                border: 'none',
                borderRadius: '0',
                backdropFilter: 'none',
                boxShadow: 'none',
                position: 'relative',
                overflow: 'visible'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  position: 'relative',
                  zIndex: 1
                }}>
                  <span className="rating-stars" style={{
                    color: '#00E6E6',
                    fontSize: '1.4em',
                    fontWeight: '700',
                    textShadow: '0 2px 8px rgba(0, 230, 230, 0.6), 0 0 20px rgba(0, 230, 230, 0.4)',
                    letterSpacing: '0.02em'
                  }}>
                    4.8 ★
                  </span>
                  <div style={{
                    width: '1px',
                    height: '24px',
                    background: 'linear-gradient(180deg, transparent, rgba(0, 230, 230, 0.5), transparent)'
                  }} />
                  <span style={{
                    color: '#ffffff',
                    fontSize: '1rem',
                    fontWeight: '600',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.01em',
                    opacity: 0.95
                  }}>
                    Trusted by <span style={{
                      color: '#00E6E6',
                      fontWeight: '700',
                      textShadow: '0 2px 6px rgba(0, 230, 230, 0.5)'
                    }}>10,000+</span> customers
                  </span>
                </div>
              </div>

              {/* View Full Collection Button */}
              <div className="premium-trust-badge" style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '1.25rem 2.5rem',
                background: 'linear-gradient(135deg, rgba(0, 230, 230, 0.1) 0%, rgba(0, 255, 255, 0.05) 100%)',
                border: '1px solid rgba(0, 230, 230, 0.3)',
                borderRadius: '16px',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 8px 32px rgba(0, 230, 230, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                position: 'relative',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onClick={() => navigate('/products')}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 230, 230, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 230, 230, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)';
              }}
              >
                {/* Subtle glow effect */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(135deg, rgba(0, 230, 230, 0.05) 0%, transparent 50%)',
                  pointerEvents: 'none'
                }} />
                
                <span style={{
                  color: '#ffffff',
                  fontSize: '1rem',
                  fontWeight: '600',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  letterSpacing: '0.01em',
                  opacity: 0.95,
                  position: 'relative',
                  zIndex: 1
                }}>
                  View <span style={{
                    color: '#00E6E6',
                    fontWeight: '700',
                    textShadow: '0 2px 6px rgba(0, 230, 230, 0.5)'
                  }}>Full</span> Collection
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Subscribe & Save Section */}
      <section className="premium-section section-padding" style={{
        position: 'relative',
        minHeight: '70vh',
        overflow: 'hidden',
      }}>
        {/* Background Video */}
        <video
          autoPlay
          muted
          loop
          playsInline
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
          onError={(e) => {
            // Fallback to image if video fails
            const img = document.createElement('img');
            img.src = "/kling_20260203_Image_to_Video___PREMIUM_4871_0.jpg";
            img.alt = "Blue subscription experience";
            img.className = "image-section-bg";
            img.style.opacity = "0.3";
            img.style.position = "absolute";
            img.style.top = "0";
            img.style.left = "0";
            img.style.width = "100%";
            img.style.height = "100%";
            img.style.objectFit = "cover";
            img.style.zIndex = "0";
            e.target.parentNode.replaceChild(img, e.target);
          }}
        >
          <source src="/chilltree-images/Subscribe section.mp4" type="video/mp4" />
        </video>

        {/* Dark Blue Gradient Overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(135deg, rgba(8, 12, 24, 0.4) 0%, rgba(15, 20, 35, 0.35) 25%, rgba(20, 28, 48, 0.3) 50%, rgba(25, 35, 60, 0.25) 75%, rgba(30, 42, 70, 0.2) 100%)',
          zIndex: 1,
        }} />

        {/* Light cyan mist */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'radial-gradient(circle at 40% 30%, rgba(0, 184, 217, 0.06) 0%, transparent 60%), radial-gradient(circle at 60% 70%, rgba(0, 184, 217, 0.04) 0%, transparent 60%)',
          zIndex: 2,
        }} />
        
        <div className="premium-container" style={{ position: 'relative', zIndex: 4 }}>
          <div className="section-header fade-in-up" style={{ animationDelay: '0.2s', textAlign: 'center' }}>
            <h2 className="section-title" style={{
              fontSize: 'clamp(2rem, 4.5vw, 2.5rem)',
              fontWeight: 800,
              marginBottom: '1.5rem',
              color: '#ffffff',
              textShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
              lineHeight: 1.2,
              letterSpacing: '-0.03em',
            }}>
              <span style={{
                color: '#00FFFF',
                textShadow: '0 4px 8px rgba(0, 0, 0, 0.3), 0 0 25px rgba(0, 255, 255, 0.5)',
              }}>Subscribe</span> & Save 25%
            </h2>
            <div className="premium-divider" />
            <p className="section-subtitle" style={{
              fontSize: 'clamp(1rem, 2vw, 1.125rem)',
              color: '#e8e8e8',
              maxWidth: '800px',
              margin: '0 auto',
              lineHeight: 1.8,
              fontWeight: 500,
            }}>
              Join our professional subscription program and enjoy premium savings
            </p>
          </div>

          <div className="premium-grid-3 fade-in-up" style={{ animationDelay: '0.4s' }}>
            <div className="glass-panel hover-lift" style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              textAlign: 'center',
              padding: '2rem'
            }}>
              <img 
                src="/chilltree-images/off25%25 new.png" 
                alt="Save 25%" 
                style={{
                  width: '80px',
                  height: '80px',
                  objectFit: 'contain',
                  marginBottom: '1.5rem'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  console.error('Image failed to load:', e.target.src);
                }}
              />
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: '#e5e7eb',
                marginBottom: '0.75rem',
                textShadow: '0 2px 4px rgba(229, 231, 235, 0.4)',
                textAlign: 'center'
              }}>
                Save 25%
              </h3>
              <p style={{
                fontSize: '0.875rem',
                color: '#00FFFF',
                lineHeight: 1.6,
                textAlign: 'center'
              }}>
                Every order, every time
              </p>
            </div>

            <div className="glass-panel hover-lift" style={{ 
              marginLeft: '1rem',
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              textAlign: 'center',
              padding: '2rem'
            }}>
              <img 
                src="/chilltree-images/Exclusive new.png" 
                alt="Exclusive Access" 
                style={{
                  width: '80px',
                  height: '80px',
                  objectFit: 'contain',
                  marginBottom: '1.5rem'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  console.error('Image failed to load:', e.target.src);
                }}
              />
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: '#e5e7eb',
                marginBottom: '0.75rem',
                textShadow: '0 2px 4px rgba(229, 231, 235, 0.4)',
                textAlign: 'center'
              }}>
                Exclusive Access
              </h3>
              <p style={{
                fontSize: '0.875rem',
                color: '#00FFFF',
                lineHeight: 1.6,
                textAlign: 'center'
              }}>
                Members get lower prices
              </p>
            </div>

            <div className="glass-panel hover-lift" style={{ 
              marginLeft: '2rem',
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              textAlign: 'center',
              padding: '2rem'
            }}>
              <img 
                src="/chilltree-images/shipping new.png" 
                alt="Free Shipping" 
                style={{
                  width: '80px',
                  height: '80px',
                  objectFit: 'contain',
                  marginBottom: '1.5rem'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  console.error('Image failed to load:', e.target.src);
                }}
              />
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: '#e5e7eb',
                marginBottom: '0.75rem',
                textShadow: '0 2px 4px rgba(229, 231, 235, 0.4)',
                textAlign: 'center'
              }}>
                Free Shipping
              </h3>
              <p style={{
                fontSize: '0.875rem',
                color: '#00FFFF',
                lineHeight: 1.6,
                textAlign: 'center'
              }}>
                On orders above $50
              </p>
            </div>
          </div>

          <div className="fade-in-up" style={{
            animationDelay: '0.8s',
            textAlign: 'center',
            marginTop: '4rem'
          }}>
            <button className="premium-btn hover-lift" style={{
              fontSize: '1.125rem',
              padding: '1.75rem 5rem',
              background: 'linear-gradient(135deg, #D4AF37 0%, #F4E4B1 100%)',
              color: '#0a1628',
              fontWeight: 700,
              letterSpacing: '0.05em',
            }}>
              Start Professional Savings
            </button>
          </div>
        </div>
      </section>

      {/* Additional Marquee Section */}
      <Marquee />

      {/* Trust Section */}
      <section className="premium-section section-padding" style={{
        position: 'relative',
        minHeight: '70vh',
        overflow: 'hidden',
      }}>
        {/* Background Image */}
        <img
          src="/woman-looking-tube-medium-shot.jpg"
          alt="Lab tested professional experience"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: 0.6,
            zIndex: 0
          }}
        />

        {/* Cinematic Blue Overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(135deg, rgba(8, 12, 24, 0.4) 0%, rgba(15, 20, 35, 0.35) 25%, rgba(20, 28, 48, 0.3) 50%, rgba(25, 35, 60, 0.25) 75%, rgba(30, 42, 70, 0.2) 100%)',
          zIndex: 1,
        }} />

        {/* Light cyan mist */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'radial-gradient(circle at 30% 20%, rgba(0, 184, 217, 0.08) 0%, transparent 50%), radial-gradient(circle at 70% 80%, rgba(0, 184, 217, 0.06) 0%, transparent 50%)',
          zIndex: 2,
        }} />
        
        <div className="premium-container" style={{ position: 'relative', zIndex: 4 }}>
          <div className="section-header fade-in-up" style={{
            animationDelay: '0.2s',
            textAlign: 'center',
            marginBottom: '3rem'
          }}>
            <h2 className="section-title" style={{
              fontSize: 'clamp(2rem, 4.5vw, 2.5rem)',
              fontWeight: 800,
              marginBottom: '1.5rem',
              color: '#ffffff',
              textShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
              lineHeight: 1.2,
              letterSpacing: '-0.03em',
            }}>
              Lab-Tested. <span style={{
                color: '#00FFFF',
                textShadow: '0 4px 8px rgba(0, 0, 0, 0.3), 0 0 25px rgba(0, 255, 255, 0.5)',
              }}>Professionally Verified</span>.
            </h2>
            <div className="premium-divider" />
          </div>

          <div className="premium-grid-3 fade-in-up" style={{ animationDelay: '0.4s' }}>
            <div className="glass-panel hover-lift" style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              textAlign: 'center',
              padding: '2rem'
            }}>
              <img 
                src="/chilltree-images/lab%20tested%20(1).png" 
                alt="Lab Tested" 
                style={{
                  width: '80px',
                  height: '80px',
                  objectFit: 'contain',
                  marginBottom: '1.5rem'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  console.error('Image failed to load:', e.target.src);
                }}
              />
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: '#e5e7eb',
                marginBottom: '0.5rem',
                textShadow: '0 2px 4px rgba(229, 231, 235, 0.4)',
                textAlign: 'center'
              }}>
                Third-Party Tested
              </h3>
              <p style={{
                fontSize: '0.875rem',
                color: '#00FFFF',
                lineHeight: 1.5,
                marginBottom: '0',
                textAlign: 'center'
              }}>
                Independent lab verification for quality and safety
              </p>
            </div>

            <div className="glass-panel hover-lift" style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              textAlign: 'center',
              padding: '2rem'
            }}>
              <img 
                src="/chilltree-images/purity%20garented%20(1).png" 
                alt="Purity Guaranteed" 
                style={{
                  width: '80px',
                  height: '80px',
                  objectFit: 'contain',
                  marginBottom: '1.5rem'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  console.error('Image failed to load:', e.target.src);
                }}
              />
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: '#e5e7eb',
                marginBottom: '0.5rem',
                textShadow: '0 2px 4px rgba(229, 231, 235, 0.4)',
                textAlign: 'center'
              }}>
                Purity Guaranteed
              </h3>
              <p style={{
                fontSize: '0.875rem',
                color: '#00FFFF',
                lineHeight: 1.5,
                marginBottom: '0',
                textAlign: 'center'
              }}>
                Premium-grade ingredients with no fillers or additives
              </p>
            </div>

            <div className="glass-panel hover-lift" style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              textAlign: 'center',
              padding: '2rem'
            }}>
              <img 
                src="/chilltree-images/award%20winning%20(1).png" 
                alt="Award Winning" 
                style={{
                  width: '80px',
                  height: '80px',
                  objectFit: 'contain',
                  marginBottom: '1.5rem'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  console.error('Image failed to load:', e.target.src);
                }}
              />
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: '#e5e7eb',
                marginBottom: '0.5rem',
                textShadow: '0 2px 4px rgba(229, 231, 235, 0.4)',
                textAlign: 'center'
              }}>
                Award Winning
              </h3>
              <p style={{
                fontSize: '0.875rem',
                color: '#00FFFF',
                lineHeight: 1.5,
                marginBottom: '0',
                textAlign: 'center'
              }}>
                Recognized excellence in botanical wellness solutions
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="premium-section section-padding" style={{
        background: 'linear-gradient(135deg, rgba(6, 12, 28, 0.98) 0%, rgba(8, 18, 38, 0.96) 25%, rgba(12, 24, 48, 0.94) 50%, rgba(6, 12, 28, 0.98) 100%)',
        backdropFilter: 'blur(40px) saturate(150%) contrast(120%)',
        WebkitBackdropFilter: 'blur(40px) saturate(150%) contrast(120%)',
        border: '1px solid rgba(0, 255, 255, 0.12)',
        borderRadius: '24px',
        position: 'relative',
        overflow: 'hidden',
        margin: '2rem auto',
        maxWidth: '1400px',
        boxShadow: '0 40px 80px rgba(0, 0, 0, 0.6), 0 0 120px rgba(0, 255, 255, 0.08), inset 0 0 60px rgba(0, 255, 255, 0.03)',
      }}>
        {/* Premium Cyan Glow Effects */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'radial-gradient(ellipse at 25% 15%, rgba(0, 255, 255, 0.08) 0%, transparent 40%), radial-gradient(ellipse at 75% 85%, rgba(0, 255, 255, 0.06) 0%, transparent 40%), radial-gradient(circle at 50% 50%, rgba(0, 255, 255, 0.02) 0%, transparent 70%)',
          zIndex: 1,
        }} />

        {/* Premium Metallic Overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(135deg, rgba(192, 192, 192, 0.04) 0%, transparent 30%, rgba(192, 192, 192, 0.02) 70%, transparent 100%)',
          zIndex: 2,
        }} />

        {/* Premium Grain Overlay */}
        <div className="grain-overlay" />

        <div className="premium-container" style={{ position: 'relative', zIndex: 4 }}>
          <div className="section-header fade-in-up" style={{ animationDelay: '0.2s', textAlign: 'center' }}>
            <h2 className="section-title" style={{
              fontSize: 'clamp(2rem, 4.5vw, 2.5rem)',
              fontWeight: 800,
              marginBottom: '1.5rem',
              color: '#ffffff',
              textShadow: '0 4px 8px rgba(0, 0, 0, 0.3), 0 0 30px rgba(0, 255, 255, 0.2)',
              lineHeight: 1.2,
              letterSpacing: '-0.03em',
            }}>
              Upgrade Your <span style={{
                color: '#00FFFF',
                textShadow: '0 4px 8px rgba(0, 0, 0, 0.3), 0 0 35px rgba(0, 255, 255, 0.6)',
              }}>Energy Routine</span>
            </h2>
            <div className="premium-divider" style={{
              background: 'linear-gradient(90deg, transparent, rgba(0, 255, 255, 0.6), transparent)',
              width: '100px',
              height: '2px',
              margin: '1.5rem auto',
            }} />
            <p className="section-subtitle" style={{
              fontSize: 'clamp(1rem, 2vw, 1.125rem)',
              color: 'rgba(255, 255, 255, 0.9)',
              maxWidth: '800px',
              margin: '0 auto',
              lineHeight: 1.8,
              fontWeight: 500,
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
            }}>
              Experience the perfect balance of nature and science. Premium wellness solutions designed for professionals who demand excellence.
            </p>
          </div>

          <div className="fade-in-up" style={{
            animationDelay: '0.6s',
            textAlign: 'center',
            marginTop: '3rem'
          }}>
            <Link
              to="/contact"
              className="premium-btn hover-lift premium-cta-btn"
              aria-label="Contact us"
              style={{
                display: 'inline-block',
                fontSize: '1.125rem',
                padding: '1.375rem 4rem',
                background: 'linear-gradient(135deg, rgba(192, 192, 192, 0.95) 0%, rgba(224, 224, 224, 0.98) 50%, rgba(192, 192, 192, 0.95) 100%)',
                color: '#0a1628',
                fontWeight: 700,
                letterSpacing: '0.05em',
                border: '1px solid rgba(0, 255, 255, 0.25)',
                borderRadius: '12px',
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3), 0 0 60px rgba(0, 255, 255, 0.15), inset 0 0 20px rgba(255, 255, 255, 0.1)',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
                textDecoration: 'none',
              }}
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}


